import React from "react";
import { Monitor, MapPin, Shuffle, CalendarDays, Users } from "lucide-react";
import { getUserTz, tzAbbr } from "@/lib/tz";

export const FORMAT_META = {
  online: { label: "Online", Icon: Monitor },
  in_person: { label: "In Person", Icon: MapPin },
  hybrid: { label: "Hybrid", Icon: Shuffle },
};

export function fmtWhen(ev) {
  const s = new Date(ev.start_utc), e = new Date(ev.end_utc);
  const timeZone = getUserTz();
  const d = s.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone });
  const t = (x) => x.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone });
  return `${d} · ${t(s)} – ${t(e)} ${tzAbbr(s, timeZone)}`;
}

const VIS_STYLE = { Private: "bg-slate-100 text-slate-700 border-slate-200", "Pending Approval": "bg-amber-50 text-amber-700 border-amber-200",
  "Approved for Group": "bg-emerald-50 text-emerald-700 border-emerald-200", "Changes Requested": "bg-orange-50 text-orange-700 border-orange-200",
  Declined: "bg-rose-50 text-rose-700 border-rose-200", Cancelled: "bg-rose-50 text-rose-700 border-rose-200" };

export function StateBadge({ ev }) {
  const st = ev.my_registration?.status;
  if (ev.agent_created && ev.is_mine && ev.visibility_label) {
    return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${VIS_STYLE[ev.visibility_label] || VIS_STYLE.Private}`} data-testid={`event-state-${ev.id}`}>{ev.visibility_label}</span>;
  }
  const map = st === "registered" ? ["Registered", "bg-emerald-50 text-emerald-700 border-emerald-200"]
    : st === "waitlisted" ? ["Waitlisted", "bg-amber-50 text-amber-700 border-amber-200"]
    : ev.status === "cancelled" ? ["Cancelled", "bg-rose-50 text-rose-700 border-rose-200"]
    : ev.manager_view ? ["Manager view · open to eligible people", "bg-indigo-50 text-indigo-700 border-indigo-200"]
    : ev.registration_state === "open" ? ["Available", "bg-sky-50 text-sky-700 border-sky-200"]
    : ev.registration_state === "waitlist" ? ["Waitlist open", "bg-amber-50 text-amber-700 border-amber-200"]
    : ev.registration_state === "full" ? ["Full", "bg-slate-100 text-slate-600 border-slate-200"]
    : ["Registration closed", "bg-slate-100 text-slate-600 border-slate-200"];
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${map[1]}`} data-testid={`event-state-${ev.id}`}>{map[0]}</span>;
}

export function EventCard({ ev, onOpen }) {
  const F = FORMAT_META[ev.format] || FORMAT_META.online;
  return (
    <button onClick={() => onOpen(ev)} data-testid={`event-card-${ev.id}`}
      className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#006090]/40 hover:shadow-sm transition-[border-color,box-shadow]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#006090]">{ev.event_type}</p>
          <h3 className="font-display font-semibold text-slate-900 mt-0.5">{ev.title}</h3>
        </div>
        <StateBadge ev={ev} />
      </div>
      <p className="text-sm text-slate-600 mt-2 flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtWhen(ev)}</p>
      <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5"><F.Icon className="w-4 h-4" />{F.label}{ev.venue_name ? ` · ${ev.venue_name}` : ""}</p>
      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{ev.registered_count} registered
        {ev.seats_left != null && ` · ${ev.seats_left} seats left`}{(ev.presenters || []).length ? ` · ${ev.presenters.join(", ")}` : ""}</p>
    </button>
  );
}
